// Client-side filtering/sorting over the product list the API already returns
// (the listing endpoints return the whole category in one response). Only
// fields that exist in the API model are used.
import { availability, displayPrice, formatINR } from "../../../Service/catalog";

const PRICE_EDGES = [500, 1000, 2000, 3000, 5000, 10000];

export const SORTS = {
  recommended: { label: "Recommended" },
  "price-asc": { label: "Price: Low to High" },
  "price-desc": { label: "Price: High to Low" },
  popular: { label: "Popular" },
};

function bucketLabel(min, max) {
  if (!min) return `Under ${formatINR(max)}`;
  if (!max) return `${formatINR(min)}+`;
  return `${formatINR(min)} – ${formatINR(max)}`;
}

function countInRange(products, min, max) {
  return products.filter((p) => {
    const price = displayPrice(p);
    return price !== null && price >= min && (max === null || price < max);
  }).length;
}

// Price ranges derived from the actual prices in the list. `countSource` lets
// counts reflect the other active filters while the options stay stable.
export function priceBuckets(products, countSource = products) {
  const edges = [0, ...PRICE_EDGES];
  return edges
    .map((min, i) => {
      const max = edges[i + 1] || null;
      return {
        id: `${min}-${max || ""}`,
        min,
        max,
        label: bucketLabel(min, max),
        total: countInRange(products, min, max),
        count: countInRange(countSource, min, max),
      };
    })
    .filter((bucket) => bucket.total > 0);
}

function parseBucket(id) {
  const [min, max] = String(id).split("-");
  return { min: Number(min) || 0, max: max ? Number(max) : null };
}

const list = (value) => (value ? value.split(",").filter(Boolean) : []);

export function parseFilters(search) {
  const params = new URLSearchParams(search);
  return {
    sub: params.has("sub") ? list(params.get("sub")) : null,
    cat: list(params.get("cat")),
    price: list(params.get("price")),
    avail: params.get("avail") === "1",
    express: params.get("express") === "1",
    sort: SORTS[params.get("sort")] ? params.get("sort") : "recommended",
  };
}

export function serializeFilters(filters) {
  const params = new URLSearchParams();
  if (filters.sub !== null && filters.sub !== undefined) params.set("sub", filters.sub.join(","));
  if (filters.cat.length) params.set("cat", filters.cat.join(","));
  if (filters.price.length) params.set("price", filters.price.join(","));
  if (filters.avail) params.set("avail", "1");
  if (filters.express) params.set("express", "1");
  if (filters.sort !== "recommended") params.set("sort", filters.sort);
  const text = params.toString().replace(/%2C/g, ",");
  return text ? `?${text}` : "";
}

export function applyFilters(products, filters, activeSubs) {
  const priceRanges = filters.price.map(parseBucket);
  return products.filter((product) => {
    if (activeSubs.length && !(product.subCategory && activeSubs.includes(product.subCategory.slug))) return false;
    if (filters.cat.length && !(product.category && filters.cat.includes(product.category.slug))) return false;
    if (filters.avail && availability(product) === "out") return false;
    if (filters.express && !product.instantDelivery) return false;
    if (priceRanges.length) {
      const price = displayPrice(product);
      if (price === null) return false;
      if (!priceRanges.some((r) => price >= r.min && (r.max === null || price < r.max))) return false;
    }
    return true;
  });
}

export function sortProducts(products, sort) {
  const indexed = products.map((product, index) => ({ product, index }));
  const price = (p) => {
    const value = displayPrice(p);
    return value === null ? Infinity : value;
  };
  if (sort === "price-asc") indexed.sort((a, b) => price(a.product) - price(b.product) || a.index - b.index);
  else if (sort === "price-desc")
    indexed.sort((a, b) => {
      const pa = price(a.product) === Infinity ? -1 : price(a.product);
      const pb = price(b.product) === Infinity ? -1 : price(b.product);
      return pb - pa || a.index - b.index;
    });
  else if (sort === "popular") {
    const score = (p) => (p.isPopular ? 2 : 0) + (p.isTrending ? 1 : 0);
    indexed.sort((a, b) => score(b.product) - score(a.product) || a.index - b.index);
  }
  return indexed.map((entry) => entry.product);
}

// Counts per option for facet lists.
export function countBy(products, key) {
  const counts = {};
  products.forEach((product) => {
    const slug = product[key] && product[key].slug;
    if (slug) counts[slug] = (counts[slug] || 0) + 1;
  });
  return counts;
}
